import { useEffect, type FC } from "react";

import { useUI } from "../contexts/ui";
import { useAnalytics } from "../utils/analytics";
import { consumeShareLinkInviteSent } from "../utils/shareLinkInviteToast";

/**
 * Shown to brand-new INVITED users immediately after the share-link
 * email gate mints them a JWT and reloads the page. The dialog stashes
 * a single-shot sessionStorage key on submit (only when the BE flips
 * `isNewUser: true`); this component reads-and-clears it on mount and
 * fires a "check your inbox" toast so the user knows an invite email
 * is on its way.
 *
 * Returning INVITED users (`isNewUser === false`) skip the stash, so
 * they never see this toast.
 *
 * Render this on whatever surface the share link first lands on — the
 * ticket view today. Safe to mount anywhere; it no-ops when there is
 * no stashed key, and the read clears the key so a future remount
 * won't double-fire.
 */
export interface ShareLinkInviteToastProps {
  /**
   * Optional ticket id passed through to PostHog so we can answer
   * "which share link drove this toast" without parsing the URL on
   * the analytics side.
   */
  ticketId?: string;
  /**
   * Optional enterprise id, same reasoning as `ticketId`.
   */
  enterpriseId?: string;
}

export const ShareLinkInviteToast: FC<ShareLinkInviteToastProps> = ({
  ticketId,
  enterpriseId,
}) => {
  const { notify } = useUI();
  const posthog = useAnalytics("ShareLinkInviteToast");

  useEffect(() => {
    if (!consumeShareLinkInviteSent()) return;

    notify({
      title: "Check your inbox",
      message:
        "We\u2019ve sent an email with instructions on how to complete your account creation.",
      variant: "success",
    });

    posthog.capture("share_link_email_sent_toast", {
      ticketId,
      enterpriseId,
    });
    // Run exactly once on mount. ticketId / enterpriseId are stable for
    // a given render of the parent surface; if they change later it's
    // because the user navigated to a different ticket and the stash
    // has already been consumed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default ShareLinkInviteToast;
